import { useEffect, useState } from "react";
import { api, roleLabel, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Archive, ArchiveRestore, Pencil, Trash2, Check, LogIn, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";

const inputCls = "border-0 shadow-none rounded-none px-0 h-9 bg-transparent font-semibold text-[16px] focus-visible:ring-0 focus-visible:ring-offset-0";

export default function UsersAdmin() {
  const { user: me, impersonate } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "loan_rep" });
  const [showArchived, setShowArchived] = useState(false);
  const [roleTab, setRoleTab] = useState("loan_rep");
  const [editing, setEditing] = useState(null); // user being edited
  const [confirmDelete, setConfirmDelete] = useState(null); // user to delete
  const load = () => {
    api.get("/users").then((r) => setUsers(r.data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("Personnel added");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "loan_rep" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleStatus = async (u) => {
    const newStatus = u.status === "suspended" ? "active" : "suspended";
    try { await api.patch(`/users/${u.id}`, { status: newStatus }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const archiveUser = async (u) => {
    try {
      if (u.status === "archived") {
        await api.post(`/users/${u.id}/unarchive`);
        toast.success("Personnel restored");
      } else {
        await api.post(`/users/${u.id}/archive`);
        toast.success("Personnel archived");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteUser = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Personnel permanently deleted");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEdit = async () => {
    if (!editing) return;
    try {
      const payload = {
        name: editing.name,
        email: editing.email,
        role: editing.role,
        phone: editing.phone || "",
        title: editing.title || "",
        company: editing.company || "",
      };
      if (editing.password) payload.password = editing.password;
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("Personnel updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const visibleUsers = showArchived ? users : users.filter((u) => u.status !== "archived");
  const roleCounts = visibleUsers.reduce((acc, u) => {
    const r = u.role || "other";
    acc[r] = (acc[r] || 0) + 1;
    return acc;
  }, {});
  const rosterUsers = visibleUsers.filter((u) => u.role === roleTab);

  return (
    <div className="max-w-[1360px] space-y-14" data-testid="users-page">
      <header className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="small-caps text-slate-500">The team</div>
          <h1 className="mt-6 font-semibold text-[72px] leading-[0.9] tracking-tight text-[#1F2937]">
            The <span className="italic">roster.</span>
          </h1>
        </div>
        <div className="flex items-center gap-8">
          <label className="inline-flex items-center gap-2 small-caps text-[#1F2937] cursor-pointer select-none" data-testid="users-show-archived-label">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="users-show-archived"
              className="h-3.5 w-3.5"
            />
            Show archived
          </label>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="group inline-flex items-center gap-4" data-testid="create-user-btn">
                <span className="h-[1px] w-16 bg-slate-800 transition-all group-hover:w-24" />
                <span className="small-caps text-[#1F2937]">Add Personnel</span>
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-slate-200 bg-white max-w-md">
              <DialogHeader>
                <div className="small-caps text-slate-500">New Personnel</div>
                <DialogTitle className="font-semibold text-[32px] text-[#1F2937] mt-2">Add member</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                <div className="border-b border-slate-200 pb-2"><div className="small-caps text-slate-500 mb-1">Name</div><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="cu-name" className={inputCls} /></div>
                <div className="border-b border-slate-200 pb-2"><div className="small-caps text-slate-500 mb-1">Email</div><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="cu-email" className={inputCls} /></div>
                <div className="border-b border-slate-200 pb-2"><div className="small-caps text-slate-500 mb-1">Temporary credential</div><Input value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder="Welcome123!" data-testid="cu-password" className={inputCls} /></div>
                <div className="border-b border-slate-200 pb-2">
                  <div className="small-caps text-slate-500 mb-1">Capacity</div>
                  <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                    <SelectTrigger data-testid="cu-role" className="border-0 shadow-none rounded-none px-0 h-9 bg-transparent font-semibold text-[16px] focus:ring-0"><SelectValue /></SelectTrigger>
                    <SelectContent>{["loan_rep","broker","admin","borrower"].map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end pt-4">
                  <button onClick={create} className="group inline-flex items-center gap-4" data-testid="cu-submit">
                    <span className="h-[1px] w-16 bg-slate-800 transition-all group-hover:w-24" />
                    <span className="small-caps text-[#1F2937]">Confirm</span>
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div>
        <div
          className="flex items-baseline gap-10 border-b border-slate-200 mb-2"
          data-testid="roster-role-tabs"
        >
          {[
            ["loan_rep", "Loan Reps"],
            ["broker", "Brokers"],
            ["borrower", "Borrowers"],
            ["admin", "Admins"],
          ].map(([role, label]) => {
            const isActive = roleTab === role;
            const count = roleCounts[role] || 0;
            return (
              <button
                key={role}
                onClick={() => setRoleTab(role)}
                data-testid={`roster-tab-${role}`}
                className={`pb-3 -mb-[1px] border-b-2 transition-colors ${
                  isActive
                    ? "border-[#1F2937] text-[#1F2937]"
                    : "border-transparent text-slate-500 hover:text-[#1F2937]"
                }`}
              >
                <span className="small-caps">{label}</span>
                <span className="ml-2 text-xs font-mono text-slate-400">{count}</span>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-12 py-3 border-b border-slate-200 small-caps text-slate-500">
          <div className="col-span-1">No.</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Capacity</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {rosterUsers.length === 0 && (
          <div className="py-12 text-center small-caps text-slate-400" data-testid="roster-empty">
            No {roleTab === "loan_rep" ? "loan reps" : roleTab + "s"} yet.
          </div>
        )}
        {rosterUsers.map((u, i) => {
          const isSelf = u.id === me?.id;
          const isArchived = u.status === "archived" || u.archived === true;
          return (
            <div
              key={u.id}
              className={`grid grid-cols-12 items-baseline py-4 border-b border-slate-200 ${isArchived ? "opacity-60" : ""}`}
              data-testid={`user-row-${u.id}`}
            >
              <div className="col-span-1 font-mono text-[11px] text-slate-400">{String(i + 1).padStart(2, "0")}</div>
              <div className="col-span-3 font-semibold text-[17px] text-[#1F2937]">
                {u.name} {isSelf && <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-2">You</span>}
                <div className="mt-0.5 small-caps text-[10px] text-slate-400">{u.status || "active"}</div>
              </div>
              <div className="col-span-4 small-caps text-slate-600 lowercase">{u.email}</div>
              <div className="col-span-2 small-caps text-slate-500">{roleLabel(u.role)}</div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                {!isSelf && !isArchived && u.role !== "admin" && (
                  <button
                    onClick={async () => {
                      if (!confirm(`Log in as ${u.name || u.email}? You'll be dropped into their session.`)) return;
                      try {
                        await impersonate(u.id);
                      } catch (e) {
                        toast.error(formatApiError(e));
                      }
                    }}
                    data-testid={`user-login-as-${u.id}`}
                    className="p-2 text-slate-500 hover:text-[#1F2937] hover:bg-slate-100 rounded"
                    title="Log in as this user"
                  >
                    <LogIn className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => setEditing({ ...u, password: "" })}
                  data-testid={`user-edit-${u.id}`}
                  className="p-2 text-slate-500 hover:text-[#1F2937] hover:bg-slate-100 rounded"
                  title="Edit personnel"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                {!isSelf && (
                  <>
                    <button
                      onClick={() => archiveUser(u)}
                      data-testid={isArchived ? `user-unarchive-${u.id}` : `user-archive-${u.id}`}
                      className="p-2 text-slate-500 hover:text-[#1F2937] hover:bg-slate-100 rounded"
                      title={isArchived ? "Restore" : "Archive"}
                    >
                      {isArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                    </button>
                    {!isArchived && (
                      <button
                        onClick={() => toggleStatus(u)}
                        data-testid={`user-toggle-${u.id}`}
                        className="p-2 text-slate-500 hover:text-[#1F2937] hover:bg-slate-100 rounded"
                        title={u.status === "suspended" ? "Reinstate" : "Suspend"}
                      >
                        {u.status === "suspended" ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmDelete(u)}
                      data-testid={`user-delete-${u.id}`}
                      className="p-2 text-slate-400 hover:text-red-700 hover:bg-red-50 rounded"
                      title="Delete permanently"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setEditing(null)}
          data-testid="user-edit-modal"
        >
          <div
            className="w-full max-w-md bg-white border border-slate-300 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="small-caps text-slate-500">Edit personnel</div>
            <h3 className="mt-2 font-semibold text-[24px] text-[#1F2937]">{editing.name}</h3>
            <div className="mt-6 space-y-5">
              <div className="border-b border-slate-200 pb-2">
                <div className="small-caps text-slate-500 mb-1">Name</div>
                <Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-user-name" className={inputCls} />
              </div>
              <div className="border-b border-slate-200 pb-2">
                <div className="small-caps text-slate-500 mb-1">Email</div>
                <Input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="edit-user-email" className={inputCls} />
              </div>
              <div className="border-b border-slate-200 pb-2">
                <div className="small-caps text-slate-500 mb-1">Capacity</div>
                <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger data-testid="edit-user-role" className="border-0 shadow-none rounded-none px-0 h-9 bg-transparent font-semibold text-[16px] focus:ring-0"><SelectValue /></SelectTrigger>
                  <SelectContent>{["loan_rep","broker","admin","borrower"].map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="border-b border-slate-200 pb-2">
                <div className="small-caps text-slate-500 mb-1">Phone</div>
                <Input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} data-testid="edit-user-phone" className={inputCls} />
              </div>
              <div className="border-b border-slate-200 pb-2">
                <div className="small-caps text-slate-500 mb-1">Title</div>
                <Input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="edit-user-title" className={inputCls} />
              </div>
              <div className="border-b border-slate-200 pb-2">
                <div className="small-caps text-slate-500 mb-1">Firm / Company</div>
                <Input value={editing.company || ""} onChange={(e) => setEditing({ ...editing, company: e.target.value })} data-testid="edit-user-company" className={inputCls} />
              </div>
              <div className="border-b border-slate-200 pb-2">
                <div className="small-caps text-slate-500 mb-1">Reset password <span className="text-slate-400">· leave blank to keep</span></div>
                <Input type="text" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} placeholder="New temporary password" data-testid="edit-user-password" className={inputCls} />
              </div>
            </div>
            <div className="mt-8 flex items-center justify-end gap-6">
              <button
                onClick={() => setEditing(null)}
                className="small-caps text-slate-500 hover:text-[#1F2937]"
                data-testid="edit-user-cancel"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                className="small-caps text-[#1F2937] inline-flex items-center gap-2"
                data-testid="edit-user-save"
              >
                <Check className="h-4 w-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="user-delete-modal"
        >
          <div
            className="w-full max-w-md bg-white border border-slate-300 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="small-caps text-red-700">Permanent deletion</div>
            <h3 className="mt-2 font-semibold text-[22px] text-[#1F2937]">Delete {confirmDelete.name}?</h3>
            <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
              This will permanently remove this personnel account.
              {confirmDelete.role === "borrower" && " All their applications, documents, and messages will also be deleted."}
              {confirmDelete.role !== "borrower" && " Their assignments on existing files will be cleared."}
              {" "}This cannot be undone.
            </p>
            <div className="mt-8 flex items-center justify-end gap-6">
              <button
                onClick={() => setConfirmDelete(null)}
                className="small-caps text-slate-500 hover:text-[#1F2937]"
                data-testid="user-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteUser(confirmDelete)}
                className="small-caps text-red-700 inline-flex items-center gap-2"
                data-testid="user-delete-confirm"
              >
                <Trash2 className="h-4 w-4" /> Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
